import {
  GetUserSettingsDocument,
  GetWorkspacesDocument,
  useUpgradePersonalWorkspaceMutation,
  LicenseRedemptionPreviewFragment,
} from '@sketch/gql-types'

import { WorkspaceCreationState } from '../../types'
import {
  formatMutationInput,
  CreateWorkspaceResponse,
} from '../useCreateWorkspace/utils'
import { ErrorHandler } from '@sketch/tracing'
import { useRequestLicenseRedemptions } from '../useRequestLicenseRedemptions'

export interface UseUpgradePersonalWorkspaceProps {
  onComplete: (workspace: CreateWorkspaceResponse) => void
  onError: (error: string) => void
  onLicensesError: (error: string) => void
  licenses: LicenseRedemptionPreviewFragment[]
}

export const useUpgradePersonalWorkspace = (
  props: UseUpgradePersonalWorkspaceProps
) => {
  const { licenses, onComplete, onError, onLicensesError } = props

  const [requestLicenseRedemptions] = useRequestLicenseRedemptions({
    onError: onLicensesError,
  })

  const [
    upgradePersonalWorkspaceMutation,
    ...mutation
  ] = useUpgradePersonalWorkspaceMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      { query: GetWorkspacesDocument },
      { query: GetUserSettingsDocument },
    ],
    onCompleted: data => {
      if (licenses && licenses.length > 0) {
        const customerId =
          data.upgradePersonalWorkspace?.workspace?.customer?.identifier

        if (!customerId) {
          ErrorHandler.shouldNeverHappen(
            `There should be always a customerId returned after upgrading a workspace`
          )
          return
        }
        requestLicenseRedemptions(
          customerId,
          licenses.map(x => x.serial)
        )
      }

      onComplete(data.upgradePersonalWorkspace.workspace)
    },
    onError: ({ message }) => {
      onError(message)
    },
  })

  const upgradePersonalWorkspace = (
    workspaceId: string,
    state: WorkspaceCreationState,
    userTosAgreed: boolean,
    userPrivacyPolicyAgreed: boolean
  ) =>
    upgradePersonalWorkspaceMutation({
      variables: {
        input: {
          ...formatMutationInput(state),
          identifier: workspaceId,
          userTosAgreed,
          userPrivacyPolicyAgreed,
        },
      },
    })

  return [upgradePersonalWorkspace, ...mutation] as const
}
